import { useCallback, useEffect, useMemo } from "react"

import { Interval } from "luxon"

import { isValidDateTimeRange } from "../date"
import { useSaveDateRange } from "./use-save-date-range"
import { useSearchParamsDateRange } from "./use-search-params-date-range"

interface UseDateRangeProps {
  /** The valid bounds of the date range */
  bounds: Interval | undefined

  /** The fallback date range to use if the current date range is invalid */
  fallbackDateRange?: Interval

  /** A unique id to identify the date range for purposes of saving */
  id: string
}

interface UseDateRangeValue {
  /** The date range's date range */
  dateRange: Interval

  /** Sets the year in both the url and its persistence layer */
  setDateRange: (value: Interval) => void
}

/**
 * Manages the date range value that powers a UI including search params,
 * saving the date range, validity, and default value.
 *
 * @example
 * const {
 *   dateRange,
 *   setDateRange
 * } = useDateRange({
 *   id: "sitesOverviewDateRange",
 *   bounds: Interval.fromDateTimes(
 *     DateTime.fromISO("2024-01-01"),
 *     DateTime.fromISO("2024-12-31"),
 *   )
 * })
 */
export const useDateRange = ({
  id,
  bounds,
  fallbackDateRange,
}: UseDateRangeProps): UseDateRangeValue => {
  const {
    isDateRangeInSearchParams,
    searchParamsDateRange,
    setSearchParamsDateRange,
  } = useSearchParamsDateRange()
  const { saveDateRange, savedDateRange } = useSaveDateRange({ id })

  const defaultFallbackDateRange: Interval = useMemo(() => {
    if (!bounds?.isValid || !bounds.start || !bounds.end) {
      return Interval.invalid("Invalid bounds")
    }

    const boundsEndYear = Interval.fromDateTimes(
      bounds.end.startOf("year"),
      bounds.end.endOf("month")
    )
    const modifiedBounds = Interval.fromDateTimes(
      bounds.start.startOf("month"),
      bounds.end.endOf("month")
    )

    return (
      boundsEndYear.intersection(modifiedBounds) ??
      Interval.invalid("Invalid bounds")
    )
  }, [bounds])

  const savedFallbackDateRange = savedDateRange?.isValid
    ? savedDateRange
    : Interval.invalid("Invalid saved date range")

  const dateRange: Interval = searchParamsDateRange?.isValid
    ? searchParamsDateRange
    : savedFallbackDateRange

  const setDateRange = useCallback(
    (value: Interval): void => {
      setSearchParamsDateRange(value)
      saveDateRange(value)
    },
    [saveDateRange, setSearchParamsDateRange]
  )

  // Handle missing search params and invalid date range
  useEffect(() => {
    if (bounds?.isValid) {
      let newDateRange: Interval = dateRange

      // Use fallback date range if range is invalid
      if (!isValidDateTimeRange(dateRange, bounds)) {
        newDateRange = fallbackDateRange?.isValid
          ? fallbackDateRange
          : defaultFallbackDateRange
      }

      // Ensure correct date range is in url
      if (
        newDateRange.isValid &&
        (newDateRange !== dateRange || !isDateRangeInSearchParams)
      ) {
        setSearchParamsDateRange(newDateRange, { replace: true })
      }

      // Ensure date range is saved if necessary
      if (newDateRange.isValid && newDateRange !== savedDateRange) {
        saveDateRange(newDateRange)
      }
    }
  }, [
    dateRange,
    isDateRangeInSearchParams,
    saveDateRange,
    savedDateRange,
    setSearchParamsDateRange,
    bounds,
    fallbackDateRange,
    defaultFallbackDateRange,
  ])

  return { dateRange, setDateRange }
}
